import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import Navigation from '../components/Navigation';
import ElegantPageBg from '../components/ElegantPageBg';
import OTPModal from '../components/OTPModal';
import TermsModal from '../components/TermsModal';
import { bookingService, paymentService, otpService } from '../services/api';
import { FiArrowLeft, FiMapPin, FiEdit2, FiCalendar, FiUsers, FiMinus, FiPlus, FiCheck } from 'react-icons/fi';
import { formatDateShort } from '../utils/date';
import './Billing.css';

const applyPromoDiscount = (subtotal, promo) => {
  if (!promo || subtotal <= 0) return { discount: 0, total: subtotal };
  const v = parseFloat(promo.discount_value) || 0;
  let discount = 0;
  if (promo.discount_type === 'percent') {
    discount = Math.min(subtotal * (v / 100), subtotal);
  } else {
    discount = Math.min(v, subtotal);
  }
  return { discount, total: Math.max(0, subtotal - discount) };
};

const Billing = () => {
  const { bookingData, updateBillingInfo } = useBooking();
  const appliedPromo = bookingData.appliedPromo || null;
  const [billingForm, setBillingForm] = useState({
    full_name: '',
    email: '',
    mobile_number: '',
    pin_code: '',
    gstin: '',
    terms_accepted: false,
  });
  const [totals, setTotals] = useState({
    subtotal: 0,
    total: 0,
    discount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [totalSyncError, setTotalSyncError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!bookingData.location || !bookingData.visitDate || !bookingData.tickets.length) {
      navigate('/tickets');
      return;
    }
    calculateTotals();
  }, [bookingData.location, bookingData.visitDate, bookingData.tickets, bookingData.addons, appliedPromo]);

  const calculateTotals = async () => {
    const items = [
      ...bookingData.tickets.map((t) => ({
        ticket_type_id: t.ticket_type_id,
        quantity: t.quantity,
        unit_price: parseFloat(t.unit_price),
      })),
      ...(bookingData.addons || []).map((a) => ({
        addon_id: a.addon_id,
        quantity: a.quantity,
        unit_price: parseFloat(a.unit_price),
      })),
    ];
    if (items.length === 0) return;
    try {
      const response = await bookingService.calculateTotal({ items });
      const d = response.data;
      let subtotal = parseFloat(d.subtotal) || 0;
      let discount = 0;
      if (appliedPromo && subtotal > 0) {
        const applied = applyPromoDiscount(subtotal, appliedPromo);
        discount = applied.discount;
      }
      const total = Math.max(0, subtotal - discount);
      setTotals({
        subtotal,
        total,
        discount,
      });
      setTotalSyncError('');
    } catch (err) {
      console.error('Error calculating totals:', err);
      setTotalSyncError('Unable to sync total from server. Please refresh and try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBillingForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const updateTicketQuantity = (index, change) => {
    navigate('/tickets');
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve();
      script.onerror = () => resolve();
      document.body.appendChild(script);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!billingForm.terms_accepted) {
      alert('Please accept the Terms & Conditions');
      return;
    }
    const mobileNumber = billingForm.mobile_number.trim();
    if (!mobileNumber || mobileNumber.length !== 10) {
      alert('Please enter a valid 10-digit mobile number');
      return;
    }
    if (totalSyncError) {
      alert(totalSyncError);
      return;
    }
    setLoading(true);
    try {
      const res = await otpService.send(mobileNumber);
      setShowOTPModal(true);
      // If backend returned OTP for testing (no WhatsApp configured), show in console for devs
      if (res.data?.otp_sent_for_testing) {
        console.info('[Testing] OTP sent for testing:', res.data.otp_sent_for_testing);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send OTP. Please try again.';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerify = async (otpCode) => {
    const mobileNumber = billingForm.mobile_number.trim();
    const bookingPayload = {
      location: bookingData.location.id,
      visit_date: bookingData.visitDate,
      number_of_visitors: bookingData.tickets.reduce((sum, t) => sum + t.quantity, 0),
      full_name: billingForm.full_name,
      email: billingForm.email,
      mobile_number: mobileNumber,
      pin_code: billingForm.pin_code || '',
      gstin: '',
      items: [
        ...bookingData.tickets.map((t) => ({
          ticket_type_id: t.ticket_type_id,
          quantity: t.quantity,
          unit_price: parseFloat(t.unit_price),
        })),
        ...(bookingData.addons || []).map((a) => ({
          addon_id: a.addon_id,
          quantity: a.quantity,
          unit_price: parseFloat(a.unit_price),
        })),
      ],
    };

    setLoading(true);
    try {
      const paymentResponse = await paymentService.createOrder(
        mobileNumber,
        otpCode,
        bookingPayload
      );
      const { order_id, amount, currency, booking_id, razorpay_key_id, internal_transaction_id: orderTxnId } = paymentResponse.data;
      const uiTotalPaise = Math.round((totals.total || 0) * 100);
      if (amount !== uiTotalPaise) {
        alert('Price was updated on server. Please review total and try again.');
        setShowOTPModal(false);
        setLoading(false);
        calculateTotals();
        return;
      }
      if (orderTxnId) {
        sessionStorage.setItem(`txn_${booking_id}`, orderTxnId);
      }

      await loadRazorpayScript();
      if (!window.Razorpay) {
        throw new Error('Failed to load Razorpay checkout');
      }

      const options = {
        key: razorpay_key_id,
        amount,
        currency,
        name: 'Ticket Booking',
        description: `Booking ID: ${booking_id}`,
        order_id,
        handler: async function (response) {
          try {
            const callbackResponse = await paymentService.callback(
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature,
              booking_id
            );
            if (callbackResponse.data.success && callbackResponse.data.booking) {
              updateBillingInfo(billingForm);
              const txnFromStorage = sessionStorage.getItem(`txn_${booking_id}`);
              const internalTxnId = callbackResponse.data.booking.internal_transaction_id || orderTxnId || txnFromStorage;
              if (txnFromStorage) sessionStorage.removeItem(`txn_${booking_id}`);
              const booking = {
                ...callbackResponse.data.booking,
                payment_id: callbackResponse.data.booking.payment_id || response.razorpay_payment_id,
                internal_transaction_id: internalTxnId,
                total_amount: callbackResponse.data.booking.total_amount ?? totals.total,
              };
              navigate('/ticket', { state: { booking } });
            } else {
              throw new Error('Payment verification failed');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            alert('Payment verification failed. Please contact support.');
            navigate('/');
          }
        },
        prefill: {
          name: billingForm.full_name,
          email: billingForm.email,
          contact: mobileNumber,
        },
        theme: { color: '#7c3aed' },
        modal: {
          ondismiss: function () {
            setLoading(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        console.error('Payment failed:', response.error);
        alert(`Payment failed: ${response.error.description || 'Unknown error'}`);
        setLoading(false);
      });

      razorpay.open();
      setShowOTPModal(false);
    } catch (error) {
      console.error('Error processing payment:', error);
      const msg =
        error.response?.data?.detail ||
        error.message ||
        'Failed to process payment. Please try again.';
      alert(msg);
      setShowOTPModal(false);
    } finally {
      setLoading(false);
    }
  };

  const totalVisitors = bookingData.tickets.reduce((sum, t) => sum + t.quantity, 0);

  return (
    <div className="billing">
      <ElegantPageBg />
      <Navigation />
      <div className="container">
        <div className="billing-content">
          <div className="left-panel">
            <div className="panel-header">
              <button className="back-button" onClick={() => navigate('/addons')}>
                <FiArrowLeft />
              </button>
              <h1 className="panel-title">Review Your Booking</h1>
            </div>

            <div className="panel-scroll">
              <div className="booking-summary-card">
                <div className="summary-header">
                  <h2 className="summary-title">Booking Summary</h2>
                  <div className="location-badge">
                    <FiMapPin />
                    <span>{bookingData.location?.city}</span>
                  </div>
                </div>
                
                <div className="summary-items">
                  <div className="summary-item">
                    <div className="item-icon">
                      <FiCalendar />
                    </div>
                    <div className="item-content">
                      <span className="item-label">Visit Date</span>
                      <span className="item-value">{formatDateShort(bookingData.visitDate)}</span>
                    </div>
                  </div>
                  
                  <div className="summary-item">
                    <div className="item-icon">
                      <FiUsers />
                    </div>
                    <div className="item-content">
                      <span className="item-label">Total Visitors</span>
                      <span className="item-value">{totalVisitors} {totalVisitors === 1 ? 'Person' : 'People'}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="ticket-summary-card">
                <div className="summary-header">
                  <h2 className="summary-title">Ticket Summary</h2>
                  <button className="edit-button" onClick={() => navigate('/tickets')}>
                    <FiEdit2 />
                    <span>Modify</span>
                  </button>
                </div>
                
                <div className="tickets-list">
                  {bookingData.tickets.map((ticket, index) => (
                    <div key={index} className="ticket-summary-item">
                      <div className="ticket-info">
                        <span className="ticket-name">Adult Ticket</span>
                        <span className="ticket-price">₹ {(ticket.unit_price * ticket.quantity).toFixed(2)}</span>
                      </div>
                      <div className="quantity-display">
                        <span className="quantity-label">Quantity:</span>
                        <span className="quantity-value">{ticket.quantity}</span>
                      </div>
                      <div className="taxes-note">(Taxes included)</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="charges-summary-card">
                <div className="charges-list">
                  <div className="charge-item">
                    <span className="charge-label">Subtotal</span>
                    <span className="charge-value">₹ {totals.subtotal.toFixed(2)}</span>
                  </div>
                  {totals.discount > 0 && appliedPromo && (
                    <div className="charge-item charge-item-discount">
                      <span className="charge-label">Discount ({appliedPromo.code})</span>
                      <span className="charge-value">- ₹ {(totals.discount || 0).toFixed(2)}</span>
                    </div>
                  )}
                </div>
                <div className="grand-total">
                  <div className="grand-total-info">
                    <span className="grand-total-label">Grand Total</span>
                    <span className="grand-total-tax-note">(inclusive of all taxes)</span>
                  </div>
                  <span className="grand-total-value">₹ {totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="right-panel">
            <div className="panel-header">
              <h1 className="panel-title">Billing Information</h1>
              <p className="panel-subtitle">Please provide your details to complete the booking</p>
            </div>

            <form onSubmit={handleSubmit} className="billing-form">
              <div className="form-scroll">
                <div className="form-group">
                  <label htmlFor="full_name">Full Name *</label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    placeholder="Enter your full name"
                    value={billingForm.full_name}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="your.email@example.com"
                    value={billingForm.email}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="mobile_number">Mobile Number *</label>
                  <div className="mobile-input-group">
                    <button type="button" className="country-code-btn">
                      <span className="flag">🇮🇳</span>
                      <span>+91</span>
                    </button>
                    <input
                      type="tel"
                      id="mobile_number"
                      name="mobile_number"
                      placeholder="9876543210"
                      value={billingForm.mobile_number}
                      onChange={handleInputChange}
                      required
                      className="form-input mobile-input"
                      maxLength="10"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="pin_code">PIN Code *</label>
                  <input
                    type="text"
                    id="pin_code"
                    name="pin_code"
                    placeholder="600001"
                    value={billingForm.pin_code}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    maxLength="6"
                  />
                </div>

                <div className="form-group checkbox-group terms-group">
                  <input
                    type="checkbox"
                    name="terms_accepted"
                    id="terms_accepted"
                    checked={billingForm.terms_accepted}
                    onChange={handleInputChange}
                    required
                    className="checkbox-input"
                  />
                  <label htmlFor="terms_accepted" className="checkbox-label">
                    I agree to the{' '}
                    <a
                      href="#"
                      className="terms-link"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowTermsModal(true);
                      }}
                    >
                      Terms & Conditions
                    </a>
                  </label>
                </div>
              </div>

              <button 
                type="submit" 
                className={`book-now-button ${billingForm.terms_accepted ? 'active' : 'disabled'}`}
                disabled={loading || !billingForm.terms_accepted || !!totalSyncError}
              >
                {loading ? (
                  <>
                    <div className="button-spinner"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <span>Confirm & Book Now</span>
                    <FiCheck className="button-icon" />
                  </>
                )}
              </button>
              {totalSyncError && (
                <div className="billing-error-note">{totalSyncError}</div>
              )}
            </form>
          </div>
        </div>
      </div>

      <OTPModal
        isOpen={showOTPModal}
        onClose={() => {
          setShowOTPModal(false);
          setLoading(false);
        }}
        mobileNumber={billingForm.mobile_number}
        onVerify={handleOTPVerify}
      />

      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
    </div>
  );
};

export default Billing;
